import {FC, ReactNode, useEffect} from 'react'
import clsx from 'clsx'
import {EditorContent, useEditor, Editor} from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import TextAlign from '@tiptap/extension-text-align'
import './TermEditor.css'

// Placeholders substituídos na geração do PDF (App\Models\Product\ContractTerms::renderContent)
const PLACEHOLDERS = [{value: '{{unidade}}', label: 'Nome da unidade'}]

type Props = {
  value: string
  onChange: (html: string) => void
  onBlur?: () => void
}

type ToolbarButtonProps = {
  title: string
  active?: boolean
  disabled?: boolean
  onClick: () => void
  children: ReactNode
}

const ToolbarButton: FC<ToolbarButtonProps> = ({title, active, disabled, onClick, children}) => (
  <button
    type='button'
    title={title}
    disabled={disabled}
    className={clsx('btn btn-sm btn-icon', active ? 'btn-light-danger active' : 'btn-active-light-danger')}
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
  >
    {children}
  </button>
)

const Toolbar: FC<{editor: Editor}> = ({editor}) => {
  const chain = () => editor.chain().focus()

  return (
    <div className='d-flex flex-wrap align-items-center gap-1 p-2 border-bottom'>
      <ToolbarButton title='Título' active={editor.isActive('heading', {level: 2})} onClick={() => chain().toggleHeading({level: 2}).run()}>
        <span className='fw-bold'>H2</span>
      </ToolbarButton>
      <ToolbarButton title='Subtítulo' active={editor.isActive('heading', {level: 3})} onClick={() => chain().toggleHeading({level: 3}).run()}>
        <span className='fw-bold'>H3</span>
      </ToolbarButton>
      <div className='vr mx-1' />
      <ToolbarButton title='Negrito' active={editor.isActive('bold')} onClick={() => chain().toggleBold().run()}>
        <i className='bi bi-type-bold fs-4' />
      </ToolbarButton>
      <ToolbarButton title='Itálico' active={editor.isActive('italic')} onClick={() => chain().toggleItalic().run()}>
        <i className='bi bi-type-italic fs-4' />
      </ToolbarButton>
      <ToolbarButton title='Sublinhado' active={editor.isActive('underline')} onClick={() => chain().toggleUnderline().run()}>
        <i className='bi bi-type-underline fs-4' />
      </ToolbarButton>
      <div className='vr mx-1' />
      <ToolbarButton title='Lista' active={editor.isActive('bulletList')} onClick={() => chain().toggleBulletList().run()}>
        <i className='bi bi-list-ul fs-4' />
      </ToolbarButton>
      <ToolbarButton title='Lista numerada' active={editor.isActive('orderedList')} onClick={() => chain().toggleOrderedList().run()}>
        <i className='bi bi-list-ol fs-4' />
      </ToolbarButton>
      <div className='vr mx-1' />
      <ToolbarButton title='Justificar' active={editor.isActive({textAlign: 'justify'})} onClick={() => chain().setTextAlign('justify').run()}>
        <i className='bi bi-justify fs-4' />
      </ToolbarButton>
      <ToolbarButton title='Alinhar à esquerda' active={editor.isActive({textAlign: 'left'})} onClick={() => chain().setTextAlign('left').run()}>
        <i className='bi bi-text-left fs-4' />
      </ToolbarButton>
      <ToolbarButton title='Centralizar' active={editor.isActive({textAlign: 'center'})} onClick={() => chain().setTextAlign('center').run()}>
        <i className='bi bi-text-center fs-4' />
      </ToolbarButton>
      <ToolbarButton title='Alinhar à direita' active={editor.isActive({textAlign: 'right'})} onClick={() => chain().setTextAlign('right').run()}>
        <i className='bi bi-text-right fs-4' />
      </ToolbarButton>
      <div className='vr mx-1' />
      <ToolbarButton title='Desfazer' disabled={!editor.can().undo()} onClick={() => chain().undo().run()}>
        <i className='bi bi-arrow-counterclockwise fs-4' />
      </ToolbarButton>
      <ToolbarButton title='Refazer' disabled={!editor.can().redo()} onClick={() => chain().redo().run()}>
        <i className='bi bi-arrow-clockwise fs-4' />
      </ToolbarButton>
      <div className='ms-auto d-flex align-items-center gap-2'>
        {PLACEHOLDERS.map((placeholder) => (
          <button
            key={placeholder.value}
            type='button'
            className='btn btn-sm btn-light'
            title={`Insere ${placeholder.value}, substituído no PDF`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => chain().insertContent(placeholder.value).run()}
          >
            + {placeholder.label}
          </button>
        ))}
      </div>
    </div>
  )
}

export const TermEditor: FC<Props> = ({value, onChange, onBlur}) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {levels: [2, 3]},
        code: false,
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
      }),
      Underline,
      // No PDF o parágrafo é justificado por padrão; só outros alinhamentos geram style inline
      TextAlign.configure({types: ['paragraph'], defaultAlignment: 'justify'}),
    ],
    content: value,
    onUpdate: ({editor}) => onChange(editor.isEmpty ? '' : editor.getHTML()),
    onBlur: () => onBlur?.(),
  })

  // Sincroniza quando o valor chega depois da montagem (ex.: edição)
  useEffect(() => {
    if (!editor) return
    const current = editor.isEmpty ? '' : editor.getHTML()
    if (value !== current) {
      editor.commands.setContent(value || '', false)
    }
  }, [value, editor])

  if (!editor) return null

  return (
    <div className='term-editor form-control form-control-solid p-0'>
      <Toolbar editor={editor} />
      <EditorContent editor={editor} />
    </div>
  )
}
